import React from 'react';
import { Loader2, Link2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

export const DEFAULT_WOWBOOKING_CONFIG = {
  mode: 'production',
  dev_api_url: '',
  dev_tenant_slug: '',
  dev_api_token: '',
  preprod_api_url: '',
  preprod_tenant_slug: '',
  preprod_api_token: '',
  production_api_url: 'https://api.app.wowbooking.ai',
  production_tenant_slug: '',
  production_api_token: '',
};

const ENV_BLOCKS = [
  {
    mode: 'dev',
    title: 'Développement',
    description: 'API WowBooking de développement (saisie manuelle des URL et identifiants).',
    urlKey: 'dev_api_url',
    slugKey: 'dev_tenant_slug',
    tokenKey: 'dev_api_token',
    urlPlaceholder: 'https://…',
  },
  {
    mode: 'preprod',
    title: 'Pré-production',
    description: 'API de pré-production.',
    urlKey: 'preprod_api_url',
    slugKey: 'preprod_tenant_slug',
    tokenKey: 'preprod_api_token',
    urlPlaceholder: 'https://…',
  },
  {
    mode: 'production',
    title: 'Production',
    description: 'API de production (wownautic.com).',
    urlKey: 'production_api_url',
    slugKey: 'production_tenant_slug',
    tokenKey: 'production_api_token',
    urlPlaceholder: 'https://api.app.wowbooking.ai',
  },
];

const MODE_LABELS = {
  dev: 'Développement',
  preprod: 'Pré-production',
  production: 'Production',
};

function EnvFields({ block, config, setConfig }) {
  return (
    <div className="space-y-4 p-4 bg-[#0B1120] rounded-lg border border-[#2D3748]">
      <div>
        <h3 className="text-white font-semibold">{block.title}</h3>
        <p className="text-xs text-gray-400 mt-1">{block.description}</p>
      </div>
      <div className="space-y-2">
        <Label className="text-white">URL de l&apos;API</Label>
        <Input
          type="url"
          placeholder={block.urlPlaceholder}
          value={config[block.urlKey]}
          onChange={(e) => setConfig({ ...config, [block.urlKey]: e.target.value })}
          className="bg-[#1A2332] border-[#2D3748] text-white"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-white">Tenant slug</Label>
        <Input
          type="text"
          placeholder="info-…"
          value={config[block.slugKey]}
          onChange={(e) => setConfig({ ...config, [block.slugKey]: e.target.value })}
          className="bg-[#1A2332] border-[#2D3748] text-white"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-white">Token API (Bearer)</Label>
        <Input
          type="password"
          placeholder="Token WowBooking"
          value={config[block.tokenKey]}
          onChange={(e) => setConfig({ ...config, [block.tokenKey]: e.target.value })}
          className="bg-[#1A2332] border-[#2D3748] text-white"
        />
      </div>
    </div>
  );
}

const AdminWowBookingTab = ({
  config,
  setConfig,
  onSave,
  onVerify,
  verifyLoading,
  verifyResult,
  isDevBackoffice,
}) => {
  return (
    <div className="space-y-4">
      <Card className="bg-[#1A2332] border-[#2D3748]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Link2 className="h-5 w-5" aria-hidden />
            WowBooking — API
          </CardTitle>
          <CardDescription className="text-gray-400">
            Choisissez l&apos;environnement actif (comme Stripe test/live). Les réservations et offres du site
            utilisent uniquement l&apos;environnement sélectionné.
            {isDevBackoffice ? (
              <span className="block mt-1 text-amber-400/90">
                Environnement site : développement — privilégiez le mode « Développement » pour vos tests.
              </span>
            ) : null}
            <span className="block mt-1 text-gray-500 text-xs">
              Après enregistrement, allez dans Catalogue → Offres et cliquez « Recharger les offres ». Vérifiez que le token correspond bien au tenant (un token invalide renvoie une liste vide ou une erreur 401).
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-white">Environnement actif</Label>
            <select
              value={config.mode}
              onChange={(e) => setConfig({ ...config, mode: e.target.value })}
              className="w-full px-3 py-2 bg-[#0B1120] border border-[#2D3748] rounded-md text-white"
            >
              <option value="dev">Développement</option>
              <option value="preprod">Pré-production</option>
              <option value="production">Production</option>
            </select>
            <p className="text-xs text-gray-400">
              Mode actif : <strong className="text-gray-200">{MODE_LABELS[config.mode] || config.mode}</strong>
            </p>
          </div>

          {ENV_BLOCKS.map((block) => (
            <EnvFields key={block.mode} block={block} config={config} setConfig={setConfig} />
          ))}

          {verifyResult ? (
            <div
              className={`p-4 rounded-lg border text-sm ${
                verifyResult.ok
                  ? 'bg-green-900/20 border-green-700 text-green-300'
                  : 'bg-red-900/20 border-red-700 text-red-300'
              }`}
            >
              {verifyResult.ok ? (
                <p>
                  Connexion OK ({MODE_LABELS[verifyResult.mode] || verifyResult.mode}) —{' '}
                  <span className="font-mono text-xs">{verifyResult.api_url}</span>
                  {verifyResult.offer_sample_count != null
                    ? ` — échantillon offres : ${verifyResult.offer_sample_count}`
                    : ''}
                </p>
              ) : (
                <p>{verifyResult.error || 'Échec de la vérification'}</p>
              )}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onVerify}
              disabled={verifyLoading}
              className="border-[#2D3748] text-white hover:bg-[#2D3748]"
            >
              {verifyLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Vérification…
                </>
              ) : (
                'Tester la connexion'
              )}
            </Button>
            <Button onClick={onSave} className="bg-blue-600 hover:bg-blue-700 text-white">
              Enregistrer la configuration
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminWowBookingTab;
